import { CategoriaEnum } from '@acme/auth'
import { AplicarSegmentoEmLote } from 'global/components/AplicarSegmentoEmLote'
import { AvisoSegmentoPendente } from 'global/components/AvisoSegmentoPendente'
import { PreLoading } from 'global/components/LoadingCustumer'
import { hasPathPermission, hasPermission } from 'hooks/useBackendMenu'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { clientes, empresas } from 'service/api'
import { useStore } from 'store'
import { NotFound } from 'templates/NotFound'
import { useAuth } from '../hooks'
import { ROTA_ACEITE_TERMOS } from './redirecionarAceiteTermos'

export function PrivateRoutes() {
    const user = useAuth()
    const navigate = useNavigate()
    const cliente = useStore(state => state.customer)

    const location = useLocation()

    const [
        isPaginaPlanos,
        isPaginaSelecionar,
        isPaginaAtendimento,
        isPaginaSuccess,
        isDownload,
        isPaginaAceiteTermos,
    ] = [
        location.pathname.includes('planos'),
        location.pathname === '/selecionar-funcionario',
        location.pathname === '/ajuda/atendimento-online',
        location.pathname === '/success',
        location.pathname.startsWith('/download/'),
        location.pathname === ROTA_ACEITE_TERMOS,
    ]

    const novo =
        cliente?.status === 'withoutSubscriptionOrCharge' &&
        user.decoded.categoria === CategoriaEnum.Cliente
    const diasAvaliacao = Number(user?.decoded?.diasAvaliacao ?? null)
    const avaliante =
        user.decoded?.categoria === CategoriaEnum.Avaliacao ||
        (user.decoded?.categoria === CategoriaEnum.Cliente && diasAvaliacao > 0)
    const naoVerificar = [
        CategoriaEnum.Administrador,
        CategoriaEnum.Suporte,
        CategoriaEnum.Funcionario,
        CategoriaEnum.Representante,
    ].includes(user.decoded?.categoria)

    // Gate 1: empresas do próprio cliente logado sem segmento (ou, no caso de
    // um Supervisor com permissão de editar Empresas, as empresas às quais
    // ele tem acesso direto). Fica de fora quem está em avaliação (categoria
    // Avaliação, ou Cliente ainda dentro do período de trial — diasAvaliacao
    // vem de avaliacoes.dtFimAvaliacao) — esse usuário não tem acesso para
    // editar a própria empresa, então não faz sentido cobrar.
    const isCliente = user.decoded?.categoria === CategoriaEnum.Cliente
    const isSupervisorComPermissao =
        user.decoded?.categoria === CategoriaEnum.Supervisor &&
        hasPermission(['update', 'Empresas'])
    const habilitarGateEmpresa =
        (isCliente && !avaliante) || isSupervisorComPermissao

    const { data: minhasEmpresas = [] } = empresas.useFetch({
        enabled: user.auth && habilitarGateEmpresa,
        refetchOnWindowFocus: false,
    })
    const empresasPendentes = habilitarGateEmpresa
        ? minhasEmpresas.filter(e => !e.hidden && !e.segmentoId)
        : []
    const empresaPendente = empresasPendentes[0]

    // Gate 2: clientes criados pelo usuário logado (representante/admin/suporte) sem segmento
    const isCriadorDeClientes = [
        CategoriaEnum.Administrador,
        CategoriaEnum.Suporte,
        CategoriaEnum.Representante,
    ].includes(user.decoded?.categoria)

    const { data: clientesPendentes = [] } =
        clientes.pendentesSegmento.useFetch({
            enabled: user.auth && isCriadorDeClientes,
            refetchOnWindowFocus: false,
        })
    const clientePendente = clientesPendentes[0]

    const emRotaObrigatoria = location.search.includes('obrigatorio=true')

    // `navigate()` durante o render é ignorado pelo react-router na primeira
    // renderização; o redirect precisa ser declarativo.
    if (user.isRestaurandoSessao) {
        return <PreLoading />
    } else if (!user.auth) {
        return <Navigate to="/" replace />
    } else if (isPaginaAtendimento || isPaginaSuccess || isPaginaAceiteTermos) {
        return <Outlet />
    } else if (
        user.auth &&
        user.decoded?.categoria === CategoriaEnum.Funcionario &&
        !user.funcionarioSelecionado &&
        !isPaginaSelecionar
    ) {
        return <Navigate to="/selecionar-funcionario" replace />
    } else if (!cliente && !naoVerificar) {
        return <PreLoading />
    } else if (empresasPendentes.length > 1 && !emRotaObrigatoria) {
        return (
            <AplicarSegmentoEmLote
                open
                titulo="Segmento das empresas"
                mensagem="Há empresas sem segmento de atuação definido. Selecione as empresas e o segmento a aplicar."
                itens={empresasPendentes.map(e => ({
                    id: e.id,
                    nomeFantasia: e.nomeFantasia,
                }))}
                onAplicar={itens =>
                    empresas
                        .aplicarSegmentoEmLote(itens)
                        .then(() => empresas.invalidateQueries())
                }
            />
        )
    } else if (empresaPendente && !emRotaObrigatoria) {
        return (
            <AvisoSegmentoPendente
                open
                nome={empresaPendente.nomeFantasia}
                onOk={() =>
                    navigate(
                        `/cadastros/empresas/${empresaPendente.id}/editar?obrigatorio=true&foco=Segmento`,
                    )
                }
            />
        )
    } else if (clientesPendentes.length > 1 && !emRotaObrigatoria) {
        return (
            <AplicarSegmentoEmLote
                open
                titulo="Segmento dos clientes"
                mensagem="Há clientes sem segmento de atuação definido. Selecione os clientes e o segmento a aplicar."
                itens={clientesPendentes.map(c => ({
                    id: c.id,
                    nomeFantasia: c.nomeFantasia,
                }))}
                onAplicar={itens =>
                    clientes
                        .aplicarSegmentoEmLote(itens)
                        .then(() =>
                            clientes.pendentesSegmento.invalidateQueries(),
                        )
                }
            />
        )
    } else if (clientePendente && !emRotaObrigatoria) {
        return (
            <AvisoSegmentoPendente
                open
                nome={clientePendente.nomeFantasia}
                onOk={() =>
                    navigate(
                        `/administracao/clientes/${clientePendente.id}/editar?obrigatorio=true&foco=Segmento`,
                    )
                }
            />
        )
    } else if (!isPaginaPlanos && novo && !avaliante) {
        return <Navigate to="/planos" replace />
    } else if (
        !hasPathPermission('read', location.pathname) &&
        !isPaginaPlanos &&
        !isDownload
    ) {
        return <NotFound />
    }

    return <Outlet />
}
